import React, { useState } from 'react';
import { clearServiceWorkerCache } from '../../utils/serviceWorker';
import { clearAll as clearOfflineQueue } from '../../utils/offlineQueueDb';

/**
 * ResetAppDataButton — gives the user a clean escape hatch when the local
 * offline copy of the app gets into a weird state (stuck queued edits, old
 * cached UI after a deploy, lingering data after a logout on a shared device).
 *
 * Behaviour:
 *   1. Tap the button → confirmation modal explains the action.
 *   2. Confirm → clear IndexedDB queue + mappings, ask the SW to drop its
 *      caches, then unregister the SW for good measure, then full reload.
 *
 * Important: no server calls. Server-side account data is never touched.
 */
const ResetAppDataButton = () => {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const performReset = async () => {
    setBusy(true);
    try {
      // 1. Wipe the offline write queue + id-mapping store.
      await clearOfflineQueue();

      // 2. Tell the SW to dump every cache it owns. Best-effort.
      try { clearServiceWorkerCache(); } catch { /* noop */ }

      // 3. Manually delete the SW caches too in case the SW isn't controlling
      //    this page yet (first-load, dev mode, etc.).
      if (typeof caches !== 'undefined' && caches.keys) {
        try {
          const keys = await caches.keys();
          await Promise.all(keys.map((k) => caches.delete(k)));
        } catch { /* noop */ }
      }

      // 4. Unregister the SW so a fresh copy installs after reload.
      if (typeof navigator !== 'undefined' && navigator.serviceWorker?.getRegistrations) {
        try {
          const regs = await navigator.serviceWorker.getRegistrations();
          await Promise.all(regs.map((r) => r.unregister()));
        } catch { /* noop */ }
      }

      // 5. Drop the IndexedDB database itself so any other stores we add later
      //    are also reset.
      if (typeof indexedDB !== 'undefined' && indexedDB.deleteDatabase) {
        try { indexedDB.deleteDatabase('edotask_offline'); } catch { /* noop */ }
      }

      // 6. Force a hard reload so the app re-fetches everything from scratch.
      window.location.reload();
    } catch (err) {
      console.error('Reset app data failed:', err);
      // Reload anyway — at worst the user sees a fresh state.
      window.location.reload();
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setConfirmOpen(true)}
        data-testid="reset-app-data-button"
        className="w-full inline-flex items-center justify-center gap-2 bg-amber-50 hover:bg-amber-100 text-amber-800 border border-amber-200 rounded-xl py-2.5 px-4 text-sm font-medium transition-colors"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
        </svg>
        Reset app data on this device
      </button>

      {confirmOpen && (
        <div
          data-testid="reset-app-data-modal"
          className="fixed inset-0 z-[101] flex items-center justify-center bg-black/50 p-4"
        >
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-5">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-amber-600 text-xl">⚠️</span>
              <h3 className="text-lg font-semibold text-gray-900">Reset app data?</h3>
            </div>
            <p className="text-sm text-gray-700 mb-4">
              This clears the local copy of your tasks, contacts and pending offline edits
              from this device. Your account on the server stays exactly as it is — nothing
              is deleted there.
            </p>
            <p className="text-xs text-gray-500 mb-4">
              Use this if the app looks stuck, an offline edit isn't syncing, or you used
              this device temporarily and want to wipe local data.
            </p>
            <div className="flex flex-col gap-2">
              <button
                onClick={performReset}
                disabled={busy}
                data-testid="reset-app-data-confirm"
                className="w-full bg-amber-600 hover:bg-amber-700 disabled:opacity-60 text-white py-2.5 rounded-xl font-medium"
              >
                {busy ? 'Resetting…' : 'Yes, reset on this device'}
              </button>
              <button
                onClick={() => setConfirmOpen(false)}
                disabled={busy}
                data-testid="reset-app-data-cancel"
                className="w-full bg-gray-200 hover:bg-gray-300 text-gray-900 py-2.5 rounded-xl font-medium"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ResetAppDataButton;
